import {
  AlertTriangle,
  ArrowUpDown,
  Atom,
  CheckCircle2,
  Clock,
  Loader2,
  Network,
  Spline,
  X,
  type LucideIcon,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import {
  useFlowRunDag,
  type FlowRunDagResponse,
  type FlowRunNodeState,
  type FlowRunSummary,
  type FlowRunSummaryState,
} from "@/api/flow-run-dag";
import { useRunVisuals } from "@/api/run-visuals";
import { ScanGeometryPanel, useScanGeometryScrub } from "@/components/affdo/ScanGeometryPanel";
import type { ScanGeometryScrub } from "@/components/affdo/ScanGeometryPanel";
import { NodeDetailBody, NodeFanoutChip } from "@/components/dag/NodeDetailSheet";
import { derivePhaseLane, type PhaseLane as PhaseLaneModel } from "@/components/dag/phase-lane";
import { FlowShapeSwitch, PhaseLane, type FlowShapeView } from "@/components/dag/PhaseLane";
import type { TileNodeData } from "@/components/dag/TileNode";
import { EmptyState } from "@/components/layout/EmptyState";
import { formatDuration } from "@/components/runs/run-duration";
import {
  deriveRunVisualViews,
  railMoleculeView,
  runMoleculeAvailability,
  runProgressKey,
  type RunVisualView,
} from "@/components/runs/run-visual-views";
import {
  deriveWorkspaceModes,
  railSectionGivesUpHeight,
  railSurvivesMaximisedMain,
  resolveWorkspaceMode,
  sideHeadingFor,
  sidePanelsFor,
  type RunWorkspaceMode,
  type RunWorkspaceModeId,
  type RunWorkspaceSideId,
} from "@/components/runs/run-workspace-modes";
import { useRunTorsionAxes } from "@/components/runs/run-torsion-axes";
import type { RunTorsionAxes } from "@/components/runs/run-torsion-axes";
import { RunFragmentFits } from "@/components/runs/RunFragmentFits";
import { RunStateBadge } from "@/components/runs/run-state-badge";
import { FRAMED_RAIL_CLASS, PAIRING_RAIL_CLASS } from "@/components/runs/torsion-fit-pairing";
import {
  describeMolecule,
  RunSubjectMolecule,
  RunVisualUnavailable,
} from "@/components/runs/RunVisualBody";
import type { TorsionPick } from "@/components/runs/torsion-selection";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { VisualPanel, VisualPanelFrame } from "@/components/viz/VisualPanel";
import { cn } from "@/lib/utils";
import { useRunSelection } from "@/stores/run-selection";

// Lazy-loaded so reactflow (~150 KB gz) only lands when a user opens
// the DAG tab, not on every run-detail page load — same rationale +
// pattern as SubmitFlow.tsx's DAG preview. A static import here would
// pull DAGCanvas into the run-detail page's own chunk even for
// visitors who never open the tab (and would stop Rollup splitting it
// out at all, since a module both statically and dynamically imported
// bundles into the static importer's chunk).
const DAGCanvas = React.lazy(() =>
  import("@/components/dag/DAGCanvas").then((m) => ({ default: m.DAGCanvas }))
);

// Lazy for the same reason, one library down: `RunTorsionPlots` reaches
// `DihedralRMSDGrid` → `DihedralProfilePlot` → recharts (~380 KB), and this
// workspace renders on every flow run's DAG tab. Without this boundary the
// torsion view is only ever ONE of the visual panel's modes, but recharts
// would be linked into the entry chunk for every route in the app
// (flowstack#992) — including the run list, which VISUALIZATION_PLAN §4 says
// the heavy chart libraries must never touch.
const RunTorsionPlots = React.lazy(() =>
  import("@/components/runs/RunTorsionPlots").then((m) => ({ default: m.RunTorsionPlots }))
);

/**
 * The run WORKSPACE (flowstack#625, #950 Part B, #956): a run-scoped DAG,
 * the visual panel and the tile settings, plus a node status table and a
 * top summary — all projected from ONE reconstructed flow-run cohort
 * (`useFlowRunDag`) rather than the template-keyed overlay the submit
 * page uses, so no other run's stale/failed state leaks in here.
 *
 * Named for what it is as of #956. It was `FlowRunDagPanel`, which had
 * stopped being true the moment the DAG became one of three parts — and
 * the name was load-bearing, not cosmetic: while the thing that expanded
 * was "the flow graph", whether the visual came along was an open
 * question. When it is "the workspace", it obviously does.
 *
 * Renders inside the run-detail page's "DAG" tab for any run whose
 * detail carries a `flow_name` (a Flow's own root run AND its
 * per-node child runs alike — `parent_run_id` in the response tells
 * the two apart, driving the breadcrumb below the summary).
 */
export function RunWorkspace({ runId }: { runId: string }): JSX.Element {
  const query = useFlowRunDag(runId);

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    const notAFlowRun =
      query.error instanceof ApiError && query.error.errorCode === "not_a_flow_run";
    return (
      <EmptyState
        title={notAFlowRun ? "Not part of a Flow." : "Could not load the flow graph."}
        description={
          notAFlowRun
            ? "This is a standalone Tile run — there's no flow-run graph to show."
            : query.error instanceof Error
              ? query.error.message
              : "Unknown error."
        }
      />
    );
  }

  const { data } = query;
  const isChildRun = data.parent_run_id !== data.run_id;

  return (
    <div className="flex flex-col gap-4">
      {/* The breadcrumb is INSIDE the banner when this is a child run — see
          `FlowRunSummaryBanner`. Two separate blocks were what let the page
          carry a green `completed` badge and a red "Failed at merge_fragments"
          with nothing joining them (flowstack#1223 A). */}
      <FlowRunSummaryBanner
        summary={data.summary}
        parentRunId={isChildRun ? data.parent_run_id : null}
        nodeName={isChildRun ? nodeNameOfRun(data) : null}
      />
      <FlowRunWorkspace data={data} />
      <FlowRunNodeTable nodes={data.nodes} />
    </div>
  );
}

/**
 * Which NODE of the flow this page's run is, or `null` when it cannot be said.
 *
 * `run_id` is only populated on a node with exactly one child (`child_count
 * === 1` — `FlowRunNodeState`), so a fanned node's sibling finds no match and
 * this returns `null` rather than the first node that looks close. The banner
 * then names the flow without naming a step, which is the weaker true claim.
 */
export function nodeNameOfRun(data: FlowRunDagResponse): string | null {
  return data.nodes.find((node) => node.run_id === data.run_id)?.name ?? null;
}

/**
 * The workspace: **a main area that is a MODE, and a side panel that follows
 * it** (flowstack#991).
 *
 * The shape this replaces was a fixed split — DAG on the left always, a 26rem
 * panel on the right carrying whatever visual the run had produced (#950 Part
 * B, #956). That gave the graph the whole main area permanently and left every
 * *visual* in a column too narrow to read: three ~90px torsion cards whose
 * status labels overlapped each other, and a 3D structure smaller than the
 * settings list beneath it. Widening the column is not the fix, because the
 * column is not the problem — the DAG holding the main area unconditionally is.
 *
 * So the user picks what the main area shows (`run-workspace-modes.ts` owns the
 * table), and the side panel becomes that thing's detail: the graph pairs with
 * tile settings as before, the plots pair with the structure, and the structure
 * pairs with the fits. The side panel is never generic, which is what makes the
 * two halves read as one surface rather than two boxes that happen to be
 * adjacent — the relationship is semantic, not just visual.
 *
 * **How the width is shared (VIS/V6 cut it, VIS/V9 re-cut it).** Side-by-side
 * starts at `pair` (960px) — a breakpoint that exists for this constraint and
 * no other, defined and derived in `tailwind.config.js` — and the rail climbs
 * with the window: 18rem there, 22rem from `xl`, 26rem from `2xl`. Before
 * VIS/V6 it was one decision doing two jobs badly: a 26rem rail is only
 * affordable once the page is wide, so demanding 26rem forced the breakpoint up
 * to `xl` and a laptop — the common case — got the STACKED arrangement. VIS/V6
 * traded that for `lg`/22rem; VIS/V9 stops deriving the breakpoint from the
 * default ladder at all, because a page-layout breakpoint has no opinion about
 * whether a curve and its structure fit beside each other, and browser zoom
 * moves the user across it (133% on a 1280px display is 962 CSS px).
 *
 * The arithmetic, since a breakpoint claim is checkable: `<main>` is the
 * viewport less the `w-56` sidebar (224px) and two `--gutter` insets (20px
 * each). At 960px that is 696px, so an 18rem (288px) rail and the 16px gap
 * leave the main pane **392px** — narrow, but a pane you can read a curve in,
 * and the floor the rail's own width is chosen against. At 1280px (`xl`) the
 * same sum gives 648px beside a 22rem rail; at 1536px (`2xl`), 840px beside a
 * 26rem one, which is the artboard's own split (`Main.dc.html` draws 348px of
 * rail against ~816px of chart). The main pane is never below 392px at any
 * width from 960 up, which is the property the ladder exists to hold —
 * `run-workspace-rail-width.test.ts` computes it rather than trusting this.
 *
 * **A narrower rail that keeps the pairing beats a wide rail that drops it.**
 * That is the whole reason 18rem appears below 1280px: the alternative was
 * holding 22rem and stacking, and a stacked pairing is not a pairing.
 *
 * **Why ONE rail width for three modes, when the 392px is argued from a chart**
 * (flowstack#1221 finding 4). The rail cannot be derived per pane: it is the
 * same element across a mode switch, and a rail that re-widths when the user
 * changes mode would move the main pane under them on every switch — a worse
 * defect than any width. So the number is picked from the BINDING mode, and
 * the chart is that mode: a dihedral curve has a real minimum readable width
 * (axis labels, the legend, and two co-plotted series that must stay
 * distinguishable), and below it the pane is not merely cramped but wrong.
 * The other two do not compete for the constraint. The DAG is pannable,
 * zoomable and refits to whatever box it is given (`useAutoFitView`), so it
 * degrades continuously rather than at a threshold — and, unlike the chart, it
 * has its own escape hatch, since maximising the main pane gives it the window
 * less the rail (`railWidthClass`, `FRAMED_RAIL_CLASS`; the 3D viewer's
 * equivalent takes the window outright, because a reading pane's rail stands
 * down where the graph's does not — flowstack#1243). Deriving 392px from the
 * DAG would therefore be deriving it from the mode with no lower bound.
 *
 * `min-w-0` on the main pane so reactflow's canvas can actually shrink —
 * without it a flex child refuses to go below its content width and the rail
 * is pushed off-screen.
 *
 * **Expanded is a FIXED VIEWPORT FRAME, not taller content** (#991's first
 * comment, and most of the fix it asks for). It used to be `fixed inset-0` on
 * this row alone with the panes free to grow, so the panes got taller than the
 * viewport and THE PAGE SCROLLED — which meant the pane headers and the mode
 * controls scrolled off the top, and what was left on screen was a canvas
 * beside a column of cards with nothing common between them. That is the whole
 * of "it looks like it is not connected". Now: the frame is fixed, the
 * maximised pane's header PINS, the pane scrolls INTERNALLY, and the page
 * itself never scrolls.
 *
 * **THE RAIL IS A STACK OF PANELS, NOT ONE SLOT (VIS/V6).** The side used to be
 * `sidePanelFor(mode.id)` — a single id, a function of the mode alone — so
 * "tile settings" and "the molecule" were mutually exclusive by construction:
 * clicking a step in the graph did not merely hide the structure, it made
 * showing both impossible, and the only route back was to leave the graph.
 * That trade is the operator's complaint. Now `sidePanelsFor` returns an
 * ORDERED LIST and each entry is its own {@link VisualPanel} in the rail, so a
 * selection APPENDS a section instead of replacing the rail — which is also
 * what the artboard draws (`Main.dc.html`: Structure, then Fit quality, then
 * Provenance, as three separate bordered cards in one 348px column).
 *
 * **EACH PANE MAXIMISES ON ITS OWN (VIZ U5.3).** The frame used to take the
 * whole workspace: one `expanded` boolean, one control, both panes side by
 * side inside it. That is the wrong unit for the thing a user is actually
 * doing — reading a fifteen-node graph, or a dihedral grid, or a structure —
 * because whichever one they came for still only got a share of the window,
 * and in the frame the share was FIXED (the aside's 26rem was no longer
 * negotiable at any width). So the state is *which pane is maximised* and
 * every pane carries its own control — the rail's sections included, which is
 * what keeps the guarantee true now that there are more than two panes.
 *
 * A closed union rather than one boolean per pane, deliberately: booleans
 * admit "both maximised", which has no meaning in a single `fixed inset-0`
 * frame, and a type that cannot express the impossible state is worth more
 * than a runtime guard against it (the same reasoning as `MAIN_SCROLLS` and
 * `SIDE_BY_MODE` being exhaustive `Record`s rather than switches).
 *
 * **The sibling pane is HIDDEN, never unmounted.** `hidden` (`display: none`)
 * keeps its React state, its react-query cache subscriptions and — the case
 * that matters — a `MoleculeViewer`'s live WebGL context alive, so restoring
 * comes back to the plot/structure the user left rather than to a reset one.
 * Unmounting would be simpler and is exactly the bug U5.3 forbids.
 *
 * **`min-h-0` is load-bearing, not tidiness.** A flex child defaults to
 * `min-height: auto` and refuses to shrink below its content, which breaks both
 * `h-full` on the nested canvas and the internal scroll this frame depends on —
 * the exact failure `AppShell.tsx` carries a warning about (PR-334 r2 #1,
 * re-verified in #982). Per-pane maximise creates TWO expanded paths where
 * there was one, and every flex child of each carries it: frame → row →
 * the maximised panel → that panel's body → the content's own fill wrapper.
 *
 * **THE CHROME IS NOW THE CONTAINER'S, NOT THIS FILE'S (VIS/V0).** Both panes
 * are `VisualPanel`s inside one `VisualPanelFrame`
 * (`components/viz/VisualPanel.tsx`), which is where the header anatomy, the
 * caption/provenance slot, the `fixed inset-0 z-50` frame, the Escape handler
 * and the page's scroll lock live. Nothing above changed meaning: the four
 * U5.3 guarantees are the container's contract now, so the NEXT scientific
 * visual inherits them instead of this file being the only place in the app
 * that has them. What stays here is what is genuinely this workspace's: which
 * pane is maximised, which mode the main area is in, and which panel pairs
 * with it. The one link in the expanded path the container cannot own is the
 * ROW between the frame and the two panels, because that row is this file's
 * layout — it carries its own `min-h-0`.
 */
function FlowRunWorkspace({ data }: { data: FlowRunDagResponse }): JSX.Element {
  const [selected, setSelected] = React.useState<TileNodeData | null>(null);
  const [pickedMode, setPickedMode] = React.useState<RunWorkspaceModeId | null>(null);
  // The fragment/torsion axes live in the RUN's selection store, not in this
  // component (flowstack#1223 E). Held outside because the Analysis tab draws
  // the same two axes for the same run and used to hold its own copy, so the
  // two tabs disagreed about which torsion was on screen with nothing saying
  // one had changed. Still the RAW pick — each reader resolves it against the
  // fragment's current torsion list (`resolveTorsionId`), which is what keeps a
  // fragment change from carrying a torsion id (or an index) into a fragment
  // that has no such torsion; the pick carries the fragment it was made in,
  // because a torsion_id is unique only within one fragment (`TorsionPick`).
  const {
    fragmentId: pickedFragment,
    torsionPick: pickedTorsion,
    selectFragment: setPickedFragment,
    selectTorsion: setPickedTorsion,
  } = useRunSelection(data.run_id);
  // WHICH pane is maximised — not two booleans (see the component docstring).
  const [maximised, setMaximised] = React.useState<MaximisedPane | null>(null);

  // The run's own progress signal, reused: `useFlowRunDag` already polls
  // every 5s while the summary is active and stops when it is terminal, and
  // every response carries each node's status. Folding those into a key is
  // what makes the visual surfaces progressive WITHOUT a second polling loop.
  const progressKey = runProgressKey(data.nodes);
  const runIsLive = LIVE_SUMMARY_STATES.has(data.summary.state);
  // Keeps the canvas's layout memo from recomputing on every render of
  // this component (selecting a node re-renders it). The canvas no
  // longer keys its stale-selection clear on this object's identity —
  // that was the PR-955 r0 defect, since a 5s poll changes the
  // reference on exactly the progress events the surface exists to show
  // — so this is now an optimisation rather than a correctness guard.
  const dag = React.useMemo(
    () => ({ flow_name: data.flow_name, nodes: data.nodes, edges: data.edges }),
    [data.flow_name, data.nodes, data.edges]
  );

  // THE PHASE LANE (VIS/V2), and which reading of the structure the DAG pane
  // is showing.
  //
  // `null` from the derivation means there is no phase read to offer — a flow
  // with no fan-out, or one whose scopes cannot be resolved from the wire (see
  // `phase-lane.ts`). Then the pane is the graph, unconditionally, and no
  // switch renders: a control with one reachable position is chrome pretending
  // to be a choice.
  //
  // The DEFAULT is the lane wherever there is one, and that is the unit: a
  // fifteen-node template renders as an unreadable thumbnail, and what a reader
  // wants first is the order of the work. The graph is one press away and is
  // still what maximises to the whole window.
  const lane = React.useMemo(
    () => derivePhaseLane(data.nodes, data.edges),
    [data.nodes, data.edges]
  );
  const [pickedShape, setPickedShape] = React.useState<FlowShapeView | null>(null);
  const shape: FlowShapeView = lane === null ? "graph" : (pickedShape ?? "lane");

  // The visuals index moved UP here from the old panel: the mode switch is
  // what needs to know which views a run can offer, and both panes now render
  // from the same answer. One query, deduped by TanStack for any component
  // that asks again with the same key.
  const visuals = useRunVisuals(data.run_id, progressKey);
  const views = deriveRunVisualViews({
    visuals: visuals.data,
    runIsLive,
    selectedNodeName: selected?.name ?? null,
  });
  const modes = deriveWorkspaceModes(views);
  const mode = resolveWorkspaceMode(modes, pickedMode);
  const sections = sidePanelsFor(mode.id, {
    hasSelection: selected !== null,
    // The RUN's molecule availability, deliberately not `views`' — that one is
    // pinned to the selected step, and a step with no structure input must not
    // make the rail's structure section disappear (flowstack#1221 finding 1).
    moleculeAvailability: runMoleculeAvailability({ visuals: visuals.data, runIsLive }),
  });
  // The RAIL's molecule, which is not `views`' (flowstack#1237). `views` is
  // pinned to the selected step, and a step with no structure input must not
  // empty the rail's structure section any more than it may remove it: the
  // section gate above and this body are the same rule asked with different
  // arguments, so they cannot disagree about whether the run has a structure.
  // Where the step DID consume one the pin still wins — see `railMoleculeView`.
  const moleculeView = railMoleculeView({
    visuals: visuals.data,
    runIsLive,
    selectedNodeName: selected?.name ?? null,
  });

  // THE TORSION AXES AND THE ANGLE SCRUB, OWNED HERE (VIS/V9).
  //
  // They used to live inside `RunTorsionPlots`, which was right while the only
  // things they drove were inside it too. They are not any more: the rail draws
  // the conformer at the scrubbed angle, so the curve in the main pane and the
  // structure beside it are two SIBLINGS that must agree about which torsion is
  // on screen and which point on it the user is standing at. One owner is what
  // makes that true by construction — the same argument that lifted the raw
  // fragment/torsion picks in #991, one level further in.
  //
  // Parked unless the plots are the main area. Opening a run on the DAG must
  // still cost zero AFFDO requests; acquiring three of them because the torsion
  // mode's rail got a new section would be a regression nobody asked for.
  const torsionMode = mode.id === "torsion";
  const axes = useRunTorsionAxes({
    runId: data.run_id,
    progressKey,
    fragmentPick: pickedFragment,
    torsionPick: pickedTorsion,
    enabled: torsionMode,
  });
  // `curveScanHashes` is deliberately not passed: `/profiles` echoes no scan
  // identity, so this surface cannot certify that the curve and the geometries
  // came from the same record, and saying nothing is how that is expressed.
  // Only the Analysis tab's materialised report can (`AnalysisTab.tsx`).
  const scrub = useScanGeometryScrub({
    runId: data.run_id,
    fragmentId: axes.fragmentId,
    torsionId: axes.torsionId,
    profile: axes.profile,
    refreshKey: progressKey,
    enabled: torsionMode,
  });

  // A maximised RAIL SECTION can stop being rendered under the user
  // (flowstack#1221 finding 3): switch mode, clear the selection, or let a poll
  // turn the molecule `absent`, and `sections` no longer carries the id
  // `pickedPane` names. Nothing would then render inside the `fixed inset-0`
  // frame — the rail wrapper drops its column width for a section that is not
  // there — leaving the user in an empty full-window frame whose only exit is
  // Escape, and whose accessible name still announces the pane that went.
  //
  // Resolved during RENDER rather than only in the effect below, so there is no
  // paint in which the frame is up with nothing in it. The effect then settles
  // the state to match; until it runs, `maximised` is the raw pick and every
  // reader below uses this.
  //
  // Cleared rather than re-targeted at another section: which one would be a
  // guess about intent, and dropping the frame returns the user to the
  // workspace they were in, which is a state they can act on. `"main"` is never
  // stale — the main pane always renders.
  const staleMaximise = maximised !== null && maximised !== "main" && !sections.includes(maximised);
  const pane = staleMaximise ? null : maximised;
  React.useEffect(() => {
    if (staleMaximise) setMaximised(null);
  }, [staleMaximise]);

  // Each pane's own name, used in FOUR places that must agree: the card's
  // `aria-label`, its control's `aria-label`/`title`, and the frame's label
  // saying which pane you are in. Derived once so they cannot drift.
  const mainName = `${mode.label} view`;
  const mainMaximised = pane === "main";
  const framed = pane !== null;
  // "Some SECTION of the rail has the frame" — not "the rail is framed",
  // because when the main pane has it the rail must go away entirely rather
  // than keep its column beside a full-window graph.
  const railMaximised = framed && !mainMaximised;
  // ...unless the main pane is the DAG, where the rail is the READOUT of the
  // click rather than a competing view (flowstack#1243 A;
  // `railSurvivesMaximisedMain` carries the argument). This is the one case in
  // which a pane holds the frame and its sibling does NOT stand down, so it is
  // named once here and read by both the wrapper's width and each section's
  // stand-down — the two mechanisms that suppressed the rail simultaneously.
  const railKeptByMain = mainMaximised && railSurvivesMaximisedMain(mode.id);
  const slots = mainPanelSlots({
    mode,
    flowName: data.flow_name,
    selectedFragmentId: pickedFragment,
  });

  // `useCallback` because `VisualPanelFrame` lists it in the dependency array
  // of the effect that registers the Escape listener: a fresh closure every
  // render would tear the listener down and re-add it on every keystroke and
  // every 5s poll response, which works but churns.
  const exitFrame = React.useCallback(() => setMaximised(null), []);

  return (
    // THE FULL-WINDOW FRAME (#991, U5.3), now the container contract's rather
    // than this file's: `fixed inset-0 z-50`, Escape, and the page's own
    // scroll lock all live in `VisualPanelFrame`. It closes WHICHEVER pane is
    // maximised, because there is only ever one — and it still cannot fight
    // `DAGCanvas`'s own key handling, since the canvas registers an Escape
    // listener only when `fullscreen` is `undefined` and this workspace always
    // passes a boolean, in BOTH pane states and while the canvas sits hidden
    // behind a maximised side panel. No sheet-first guard is needed either:
    // the node detail renders in the side panel here, never as a radix dialog
    // (see `DAGCanvasProps.onNodeSelect`), so there is nothing to close first.
    <VisualPanelFrame
      active={framed}
      // Names WHICH pane is expanded, not just that something is: with several
      // maximisable panes "workspace, expanded" left a screen-reader user
      // unable to tell what they had just entered.
      label={`${data.flow_name} workspace, ${
        pane === null || pane === "main" ? mainName : sideNameFor(pane)
      } expanded`}
      onExit={exitFrame}
    >
      <div
        className={cn(
          "flex flex-col gap-4 pair:flex-row pair:items-stretch",
          // Expanded: a row at EVERY width — the stacked arrangement exists to
          // protect a narrow *page*, and a full-screen frame is not one — and
          // `min-h-0` so the maximised pane can shrink inside the frame. This
          // element sits BETWEEN the frame and the panels, so it is the one
          // link in the expanded path the container cannot carry for us.
          framed && "min-h-0 flex-1 flex-row"
        )}
      >
        <VisualPanel
          as="section"
          label={mainName}
          // The mode switch takes the heading's slot because it IS the
          // heading: it names what the pane is showing and changes it. A
          // separate title beside it would say the same word twice.
          titleControl={
            /* The mode switch as a segmented control: ONE track cut a step
               into the panel (`bg-panel-well`), with the active segment
               raised back out of it to panel level. The loose pills this
               replaces gave the active mode no ground to be raised from, so
               "which view am I in" rested entirely on a border-and-ink
               difference between three free-floating chips.

               SEMANTICS ARE DELIBERATELY UNCHANGED — still a `role="group"`
               of `aria-pressed` buttons, not a radiogroup, and the dispatch
               asked for the reasoning rather than a default. A radiogroup is
               the right pattern when the options are a *value being chosen*
               and the group is one tab stop with arrow-key roving focus. This
               control is neither: each button acts on press (there is no
               committed selection separate from the act), and modes can be
               individually DISABLED when a run has not produced that visual
               yet — a state radio semantics handles badly and roving focus
               handles worse, since the disabled member is the one focus must
               skip. Adopting it would also have meant writing the key
               handling, which is behaviour, and this unit is chrome. */
            <div
              className="flex flex-wrap items-center gap-0.5 rounded-lg border border-border-subtle bg-panel-well p-[3px]"
              role="group"
              aria-label="View"
            >
              {modes.map((m) => (
                <ModeButton
                  key={m.id}
                  mode={m}
                  active={m.id === mode.id}
                  onSelect={() => setPickedMode(m.id)}
                />
              ))}
            </div>
          }
          identifier={slots.identifier}
          caption={
            slots.caption === null ? undefined : (
              <>
                {slots.caption.text}{" "}
                {/* `break-all`: a sha256 is 71 characters and will not fit the
                    26rem column on one line, and a hash that overflows its box
                    is one a reader cannot copy. */}
                <span className="break-all font-mono">{slots.caption.mono}</span>
              </>
            )
          }
          controls={
            <>
              {/* PIPELINE vs GRAPH, in the DAG mode only and only where a lane
                  exists. It sits in the pane's controls rather than beside the
                  mode switch because it is not a fourth MODE — the pane is
                  still "the flow's structure", and this chooses how that
                  structure is read. */}
              {mode.id === "dag" && lane !== null && (
                <FlowShapeSwitch view={shape} onSelect={setPickedShape} />
              )}
              {/* The pin is only visible in the DAG's own side panel, so in a
                  visual mode it needs saying here — otherwise a structure
                  silently pinned to a step reads as the run's ligand, which is
                  the unverifiable render the fragment-vs-ligand rule exists to
                  prevent. */}
              {selected !== null && mode.id !== "dag" && (
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  aria-label={`Clear the selected step ${selected.name}`}
                  className="inline-flex items-center gap-1 rounded-full border border-border-subtle bg-surface-2 px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground"
                >
                  <span className="font-mono">{selected.name}</span>
                  <X className="h-3 w-3" aria-hidden />
                </button>
              )}
            </>
          }
          maximised={mainMaximised}
          onToggleMaximise={() => setMaximised(mainMaximised ? null : "main")}
          // HIDDEN, not unmounted — the canvas keeps its viewport and the
          // plots keep their selection while a rail section has the frame.
          standDown={railMaximised}
          // Only the plots scroll: the graph and the 3D viewer FILL their box
          // (a scrollbar on a pannable canvas is a trap), while a fragment's
          // dihedral grid is a list that can exceed any frame.
          scrolls={mainScrolls(mode.id, shape)}
          className="min-w-0 flex-1"
        >
          <WorkspaceMain
            mode={mode}
            dag={dag}
            lane={lane}
            shape={shape}
            runIsLive={runIsLive}
            maximised={mainMaximised}
            selectedNodeName={selected?.name ?? null}
            onNodeSelect={setSelected}
            axes={axes}
            scrub={scrub}
            onFragmentSelect={setPickedFragment}
            onTorsionSelect={setPickedTorsion}
          />
        </VisualPanel>

        {/* THE RAIL. A column of `VisualPanel`s rather than one panel, so a
            selected step can be read WITHOUT giving up the structure (VIS/V6).
            The wrapper is plain layout — it paints nothing, adds no second
            panel vocabulary beside the container, and exists only to give the
            sections their column and their gap.

            It carries THREE width states rather than the panel's two — see
            {@link railWidthClass}. */}
        <div className={cn("flex min-w-0 flex-col gap-4", railWidthClass(pane, mode.id))}>
          {sections.map((section) => {
            const heading = sideHeadingFor(section);
            const sectionMaximised = pane === section;
            // flowstack#1243 B. A section with the tile settings under it gives
            // up height so the readout of the click begins inside the window —
            // never the maximised one, which IS the frame.
            const capped =
              !sectionMaximised &&
              railSectionGivesUpHeight({
                side: section,
                sections,
                hasSelection: selected !== null,
              });
            return (
              <VisualPanel
                key={section}
                as="aside"
                label={`${heading} panel`}
                title={heading}
                // The control has to say "side panel" where the landmark says
                // "panel": "Expand the Tile settings panel" would not tell a
                // user which of the panes they were about to expand.
                paneName={sideNameFor(section)}
                controls={
                  section === "tile" && selected !== null ? (
                    <button
                      type="button"
                      onClick={() => setSelected(null)}
                      aria-label="Clear the selected step"
                      className="rounded-md px-1.5 py-0.5 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Clear
                    </button>
                  ) : undefined
                }
                maximised={sectionMaximised}
                onToggleMaximise={() => setMaximised(sectionMaximised ? null : section)}
                // Every OTHER panel stands down while one has the frame —
                // `hidden`, never unmounted, so the molecule's WebGL context
                // survives a trip through the tile settings and back. The
                // MECHANISM is untouched by #1243; what changed is WHICH
                // sections it applies to, since a maximised DAG keeps its rail.
                standDown={framed && !sectionMaximised && !railKeptByMain}
                scrolls
                bodyClassName={capped ? RAIL_CAP_CLASS : undefined}
                // Maximised, a section is the FRAME, so it drops the rail's
                // column width — a detail panel pinned to 18rem inside a
                // full-screen frame would be the narrow column #991 set out to
                // abolish, just with more empty space beside it. Unmaximised it
                // sets no width at all: the wrapper above owns the rail's.
                className={sectionMaximised ? "w-full flex-1" : undefined}
              >
                <WorkspaceSide
                  side={section}
                  node={selected}
                  flowName={data.flow_name}
                  runId={data.run_id}
                  progressKey={progressKey}
                  runIsLive={runIsLive}
                  moleculeView={moleculeView}
                  scrub={scrub}
                  maximised={sectionMaximised}
                  capped={capped}
                  showFragmentList={mode.id === "molecule"}
                  selectedFragmentId={pickedFragment}
                  onFragmentSelect={setPickedFragment}
                  selectedTorsion={pickedTorsion}
                />
              </VisualPanel>
            );
          })}
        </div>
      </div>
    </VisualPanelFrame>
  );
}

/**
 * What the MAIN pane's identifier and caption slots say, per mode.
 *
 * Pure, and exported for its own test, for the reason `describeUnavailable` is
 * (Rupert PR-999 r1): these are CLAIMS about somebody's run, and a claim is
 * worth pinning by itself rather than only through a rendered component.
 *
 * **A slot is filled only where the run really answers it.** The DAG knows the
 * template it is an instance of; the molecule knows the artifact it drew and
 * where that artifact came from; the plots know the fragment the user picked,
 * and nothing until they do. Everything else returns `null` and the slot does
 * not render — an empty provenance slot is honest, and a decorated one is a
 * claim nobody checked.
 */
export function mainPanelSlots(args: {
  mode: RunWorkspaceMode;
  flowName: string;
  selectedFragmentId: string | null;
}): {
  identifier: string | null;
  /** Prose, plus the identifier a reader would copy (rendered mono). */
  caption: { text: string; mono: string } | null;
} {
  const { mode, flowName, selectedFragmentId } = args;
  if (mode.id === "dag") return { identifier: flowName, caption: null };
  if (mode.id === "torsion") return { identifier: selectedFragmentId, caption: null };

  const artifact = mode.view?.artifact ?? null;
  if (artifact === null) return { identifier: null, caption: null };
  // `origin` is a closed two-set on the wire, so this is exhaustive rather
  // than a default. The distinction is the one `pickSubjectArtifact` exists to
  // preserve: an AFFDO run's first PRODUCED molecule is a fragment of the
  // ligand, so "consumed" and "produced" are different molecules, not
  // different words for the same one.
  const text =
    artifact.origin === "input"
      ? "Consumed by this run; produced outside it."
      : `Produced by ${artifact.node_name ?? "this run"}.`;
  return { identifier: null, caption: { text, mono: artifact.content_hash } };
}

/**
 * Which of the workspace's panes has the frame (VIZ U5.3, widened by VIS/V6).
 *
 * A closed union — `null | "main" | <a rail section>` — rather than one boolean
 * per pane. There is exactly one `fixed inset-0` frame, so "two maximised" is
 * not a state the UI has; expressing it as booleans would make it
 * representable and then need a runtime guard to keep it from happening. This
 * makes it unspellable instead.
 *
 * It reuses `RunWorkspaceSideId` verbatim rather than pairing `"side"` with a
 * section id, which is what keeps `pane === section` a direct comparison
 * inside the rail's `.map`. The two vocabularies cannot collide: `"main"` is
 * not a side id, and adding one that IS would be a compile error here.
 *
 * **A value here is a PICK, not a guarantee the pane exists.** The rail's
 * contents are derived (`sidePanelsFor`), so a section can stop being rendered
 * while it holds the frame — see `staleMaximise` in `FlowRunWorkspace`, which
 * resolves the pick against the rail on every render rather than trusting the
 * stored id. Making the type carry that guarantee would mean re-deriving the
 * union per render, which TypeScript cannot express over a runtime array.
 */
type MaximisedPane = "main" | RunWorkspaceSideId;

/**
 * What the maximise control calls a rail section — "Expand the {…}".
 *
 * Distinct from the landmark's own name (`"{heading} panel"`) on purpose: the
 * landmark says which panel it is, while the CONTROL has to say which of the
 * panes on screen it acts on, and "Expand the Tile settings panel" answers the
 * first question where the user is asking the second.
 */
function sideNameFor(section: RunWorkspaceSideId): string {
  return `${sideHeadingFor(section)} side panel`;
}

/**
 * The rail wrapper's width, which is THREE states rather than the two a single
 * panel has — because with a stack the maximised element is inside the
 * wrapper rather than being it.
 *
 * - **nothing maximised** — the rail's own column: full width stacked, 18rem
 *   from `pair`, 22rem from `xl`, 26rem from `2xl` (`FlowRunWorkspace`'s
 *   docstring carries the arithmetic).
 * - **the MAIN pane has the frame, and it is a READING pane** — `hidden`
 *   outright. Standing every section down is not sufficient: an empty rail box
 *   still holds its column of the frame open beside a full-window structure,
 *   which is the narrow-column failure #991 set out to abolish wearing a
 *   different disguise.
 * - **the MAIN pane has the frame, and it is the DAG** — the rail SURVIVES, at
 *   the same ladder without its stacked step (flowstack#1243 A;
 *   `railSurvivesMaximisedMain` carries the argument, `FRAMED_RAIL_CLASS` the
 *   widths). It scrolls internally, because the frame is exactly the viewport
 *   and a rail that overflowed it would put the tile settings out of reach
 *   again by a second route — the page itself cannot scroll here.
 * - **a rail SECTION has the frame** — the wrapper becomes the frame's flex
 *   child, so it needs `min-h-0` (the link in the expanded path that the
 *   container cannot own for us) and must drop its column width.
 *
 * Written as an exhaustive function rather than inline conditionals so the
 * four states are named and cannot silently overlap — the first cut applied
 * `hidden` AND the column width together, which happened to work only because
 * `display: none` beats a width.
 */
function railWidthClass(maximised: MaximisedPane | null, mode: RunWorkspaceModeId): string {
  if (maximised === null) return PAIRING_RAIL_CLASS;
  if (maximised === "main") {
    return railSurvivesMaximisedMain(mode)
      ? cn(FRAMED_RAIL_CLASS, "min-h-0 overflow-y-auto")
      : "hidden";
  }
  return "min-h-0 w-full flex-1";
}

/**
 * What a rail section is capped to when it has the tile settings under it
 * (flowstack#1243 B — `railSectionGivesUpHeight` decides WHICH sections).
 *
 * **Viewport-relative rather than a pixel box, and that is the whole reason it
 * works.** The rail's top is roughly fixed page chrome (measured at 249px:
 * masthead, run header, tab bar), while the height it has to fit into is the
 * viewport. A `max-h-[420px]` cap would be generous on a laptop and useless on
 * anything shorter; `38vh` shrinks with the window it is competing for. At the
 * reported 679px viewport that is 258px, so the tile settings begin at
 * 249 + 258 + 16 (the `gap-4`) = 523 — 156px inside the fold, where they used
 * to start 103px below it. On a 1400px screen the same cap is 532px and the
 * structure barely gives anything up.
 *
 * **A DEFINITE height, not a `max-h`, and that distinction is load-bearing.**
 * A capped body holds a child told to `fill` — `flex-1 min-h-0` all the way
 * down to the 3Dmol mount, which has no intrinsic height of its own. Under a
 * `max-height` there is nothing for `flex-1` to be a fraction OF, so the chain
 * resolves to the content height and the whole section collapses: measured at
 * 99px tall, a molecule panel with no molecule in it. `h-[38vh]` gives the
 * flex basis something to divide, and `min-h-0` beside it is what lets the
 * child shrink into it at all (the failure `AppShell.tsx` carries its warning
 * about). `overflow-y-auto` is for the sections that CANNOT shrink — the fit
 * numbers, the scan geometry — so a capped list is scrolled inside its box
 * rather than clipped by the card's `overflow-hidden`.
 */
const RAIL_CAP_CLASS = "h-[38vh] min-h-0 overflow-y-auto";

/**
 * Whether the main area's body scrolls internally when the MAIN pane is
 * maximised.
 *
 * A `Record`, so a mode added without deciding this is a type error rather
 * than a silent default.
 */
const MAIN_SCROLLS: Record<RunWorkspaceModeId, boolean> = {
  dag: false,
  molecule: false,
  torsion: true,
};

/**
 * The same question asked of the DAG mode, which is now TWO surfaces
 * (VIS/V2).
 *
 * `MAIN_SCROLLS` stays the mode-level table — a mode added without deciding
 * this is still a type error — and this is the one mode whose answer depends on
 * something below the mode. The distinction is the same one the table already
 * draws between the canvas and the plots: the graph is pannable and fills its
 * box, so a scrollbar on it is a trap, while the phase lane is a wrapping list
 * that can exceed any frame exactly the way the dihedral grid can.
 */
function mainScrolls(mode: RunWorkspaceModeId, shape: FlowShapeView): boolean {
  if (mode === "dag") return shape === "lane";
  return MAIN_SCROLLS[mode];
}

/** The main area — one of three things, chosen by the mode. */
function WorkspaceMain({
  mode,
  dag,
  lane,
  shape,
  runIsLive,
  maximised,
  selectedNodeName,
  onNodeSelect,
  axes,
  scrub,
  onFragmentSelect,
  onTorsionSelect,
}: {
  mode: RunWorkspaceMode;
  dag: Pick<FlowRunDagResponse, "flow_name" | "nodes" | "edges">;
  /** The phase read of `dag`, or `null` where there is none — `phase-lane.ts`. */
  lane: PhaseLaneModel | null;
  /** Which reading of the structure the DAG mode is showing. */
  shape: FlowShapeView;
  runIsLive: boolean;
  /** THIS pane has the frame — never merely "the workspace is framed". */
  maximised: boolean;
  selectedNodeName: string | null;
  onNodeSelect: (node: TileNodeData | null) => void;
  /** Resolved once above and shared with the rail's structure section — see
   * `FlowRunWorkspace`. */
  axes: RunTorsionAxes;
  scrub: ScanGeometryScrub;
  onFragmentSelect: (fragmentId: string) => void;
  onTorsionSelect: (pick: TorsionPick) => void;
}): JSX.Element {
  if (mode.id === "dag" && shape === "lane" && lane !== null) {
    // Clicking a step in the lane is the graph's own affordance kept: the rail
    // appends that step's Tile settings, exactly as a node click does. The
    // lookup is here because the lane deals in NAMES and the rail wants the
    // node — the same split `DAGCanvas` keeps, where the canvas owns the node
    // object and the parent only ever names one back.
    return (
      <PhaseLane
        lane={lane}
        label={`${dag.flow_name} pipeline`}
        selectedStep={selectedNodeName}
        onSelectStep={(name) => {
          const node = dag.nodes.find((n) => n.name === name);
          if (node === undefined) return;
          onNodeSelect({
            name: node.name,
            tile: node.tile,
            fanout_kind: node.fanout_kind,
            status: node.status,
            run_id: node.run_id,
            cache_hit: node.cache_hit,
          });
        }}
      />
    );
  }
  if (mode.id === "dag") {
    return (
      <React.Suspense fallback={<Skeleton className="h-[360px] w-full" />}>
        <DAGCanvas
          dag={dag}
          onNodeSelect={onNodeSelect}
          selectedNodeName={selectedNodeName}
          // Defined unconditionally, so the canvas never renders a toggle of
          // its own (nor an Escape handler): the control lives in the pane
          // header now, where it survives a switch away from the graph. See
          // `DAGCanvasProps`.
          //
          // It is the MAIN pane's own state, not "something is expanded" —
          // that is what re-fits the graph when the graph's pane takes the
          // frame, and leaves the viewport alone when the SIDE panel does
          // (`useAutoFitView` keys on this flag beside the measured size).
          fullscreen={maximised}
        />
      </React.Suspense>
    );
  }
  // `resolveWorkspaceMode` only returns a `ready` visual mode, so this is a
  // guard against a mode going stale between render and paint rather than an
  // expected path — but it must say WHICH of pending/absent it is, never draw
  // an empty frame (flowstack#923).
  if (mode.view === null || mode.view.availability !== "ready") {
    return mode.view === null ? (
      <EmptyState title="Nothing to show." description="This view has no data." />
    ) : (
      <RunVisualUnavailable view={mode.view} runIsLive={runIsLive} />
    );
  }
  if (mode.id === "torsion") {
    return (
      <React.Suspense fallback={<Skeleton className="h-48 w-full" />}>
        <RunTorsionPlots
          axes={axes}
          scrub={scrub}
          onFragmentSelect={onFragmentSelect}
          onTorsionSelect={onTorsionSelect}
        />
      </React.Suspense>
    );
  }
  return (
    <RunSubjectMolecule
      contentHash={mode.view.artifact?.content_hash ?? null}
      // The main area IS the frame here, so the viewer takes all of it.
      fill={maximised}
      siblingCount={mode.view.siblingInputCount ?? 0}
      // `deriveRunVisualViews` never falls back here, so this mode's view
      // carries no `subjectFallbackFromNode` and the shared description reduces
      // to the pin or the run subject — which is the point of sharing it.
      {...describeMolecule(mode.view)}
    />
  );
}

/** ONE section of the rail — the body of whichever panel is asking. */
function WorkspaceSide({
  side,
  node,
  flowName,
  runId,
  progressKey,
  runIsLive,
  moleculeView,
  scrub,
  maximised,
  capped,
  showFragmentList,
  selectedFragmentId,
  onFragmentSelect,
  selectedTorsion,
}: {
  side: RunWorkspaceSideId;
  node: TileNodeData | null;
  flowName: string;
  runId: string;
  progressKey: string;
  runIsLive: boolean;
  /** Non-nullable since flowstack#1237: `railMoleculeView` always answers,
   * with the run-level view when neither the pin nor the subject is
   * renderable, so there is no "no view at all" state for the rail to render. */
  moleculeView: RunVisualView;
  /** The angle scrub the CURVES in the main pane are annotated from — the same
   * object, so the structure this section draws is the point the user is
   * standing on rather than a second reading of it (VIS/V9). */
  scrub: ScanGeometryScrub;
  /** THIS pane has the frame — never merely "the workspace is framed". */
  maximised: boolean;
  /**
   * This section's height is CAPPED so the tile settings under it start on
   * screen (flowstack#1243 B). It means the same thing to a body that can
   * shrink as `maximised` does — take the box you are given rather than a fixed
   * one — which is why the molecule reads the two together rather than
   * scrolling a 420px viewer inside a 258px window.
   */
  capped: boolean;
  /**
   * Whether the fits section offers the whole selectable fragment LIST.
   *
   * Decided by the caller from the MODE rather than read off `side`, which is
   * what it amounted to before VIS/V6 split the structure and the numbers into
   * separate sections: the list belongs beside a molecule that has the main
   * area (pick a fragment, see its structure), and beside the plots the
   * fragment is already picked by the axis rows above the curves.
   */
  showFragmentList: boolean;
  selectedFragmentId: string | null;
  onFragmentSelect: (fragmentId: string) => void;
  /** The workspace's raw torsion pick — the fits section resolves it itself,
   * so it names the torsion the plots are actually showing. */
  selectedTorsion: TorsionPick | null;
}): JSX.Element {
  if (side === "tile") {
    return <NodeInfoBody node={node} flowName={flowName} />;
  }
  if (side === "structure") {
    // THE CONFORMER AT THE SCRUBBED ANGLE, beside the curve it belongs to
    // (VIS/V9). This is the section the whole unit is about: the panel used to
    // render UNDER the grid inside the main pane, which at 1200px put it below
    // the fold — so the one thing the mechanic exists to show, the geometry
    // moving with the point, could not be seen at the moment the point moved.
    //
    // Its heading is the container's here, so the panel does not print its own
    // (`showHeading`), and it renders in BOTH of the plots' views rather than
    // only the focused one. The overview marks the same selected torsion, and a
    // rail section that empties itself when you press "Overview" is a section
    // that moves the layout under the user for no gain.
    return <ScanGeometryPanel showHeading={false} {...scrub.panel} />;
  }
  if (side === "molecule") {
    // The structure — the reference beside the curves, and now beside the
    // GRAPH too, which is the section a step selection no longer costs you.
    //
    // **Not deferred behind a first-reveal mount, deliberately** (flowstack#1221
    // finding 2). The concern is real in kind — 3Dmol is WebGL and DAG mode now
    // mounts it on arrival where the tab used to mount nothing — but the remedy
    // proposed does not apply to this placement: the section is at the TOP of
    // the rail and visible the instant the tab renders, so "mount on first
    // reveal" and "mount now" are the same moment. The costs that CAN be
    // avoided already are: the ~530 KB of 3Dmol.js sits behind
    // `MoleculeViewerPanel`'s `React.lazy` chunk boundary and lands only when a
    // viewer actually mounts, and as of finding 1 a run with no structure
    // mounts none at all, because the section is no longer in the rail. What is
    // left is one WebGL context per DAG-tab visit on a run that HAS a
    // structure — which is the operator's explicit request rendered, not an
    // accident. Deferring that further would take an intersection observer on a
    // pane that is never off-screen, and it is not measured here: the argument
    // above does not turn on the size of the number.
    if (moleculeView.availability !== "ready") {
      // Reached only when the RUN has no structure at all — `railMoleculeView`
      // has already fallen back to the run subject for a step that consumed
      // none, so this is never a step's own emptiness (flowstack#1237). The
      // section itself is gone by then unless the run is live, and "not yet"
      // is a sentence this rail should say rather than swallow.
      return <RunVisualUnavailable view={moleculeView} runIsLive={runIsLive} />;
    }
    return (
      <RunSubjectMolecule
        contentHash={moleculeView.artifact?.content_hash ?? null}
        // `capped` as well as `maximised`: both mean "your box is decided for
        // you". Without it the viewer keeps its fixed 420px inside a 38vh
        // window and the structure is scrolled rather than smaller — a
        // half-clipped molecule, which is a worse answer than a small one.
        fill={maximised || capped}
        siblingCount={moleculeView.siblingInputCount ?? 0}
        {...describeMolecule(moleculeView)}
      />
    );
  }
  // The numbers. Beside the plots they are about THIS plot — the selected
  // fragment's own figures, naming the torsion on screen (VIZ U5.1). Beside a
  // molecule that has the main area the whole list comes too, so picking a
  // fragment here is the same selection the plots use when the user switches.
  return (
    <RunFragmentFits
      runId={runId}
      progressKey={progressKey}
      runIsLive={runIsLive}
      selectedFragmentId={selectedFragmentId}
      onFragmentSelect={onFragmentSelect}
      selectedTorsion={selectedTorsion}
      showList={showFragmentList}
    />
  );
}

/**
 * The tile settings — what the overlay sheet used to carry.
 *
 * Renders `NodeDetailBody`, the same component the sheet renders, so the two
 * surfaces cannot drift. Only the heading chrome differs, and that now lives
 * in the side pane's pinned header.
 */
function NodeInfoBody({
  node,
  flowName,
}: {
  node: TileNodeData | null;
  flowName: string;
}): JSX.Element {
  return (
    <div className="flex flex-col">
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex flex-wrap items-baseline gap-2">
          <h3 className="font-display text-base">{node?.name ?? "Step"}</h3>
          {node?.fanout_kind && <NodeFanoutChip fanoutKind={node.fanout_kind} />}
        </div>
        <span className="truncate font-mono text-xs text-muted-foreground">
          {node?.tile ?? "—"}
        </span>
      </div>
      <NodeDetailBody
        nodeName={node?.name ?? null}
        tileName={node?.tile ?? null}
        fanoutKind={node?.fanout_kind ?? null}
        status={node?.status ?? null}
        runId={node?.run_id ?? null}
        flowName={flowName}
      />
    </div>
  );
}

/**
 * The glyph each mode wears in the switch.
 *
 * A `Record` over the mode ids for the same reason `MAIN_SCROLLS` is one: a
 * mode added without deciding its icon is a type error, not a segment that
 * silently renders as a bare label beside two that do not. It lives here
 * rather than on `RunWorkspaceMode` deliberately — `run-workspace-modes.ts`
 * is a pure module about which view resolves and which panel pairs with it,
 * and an icon component is presentation this file already owns.
 *
 * They are a SECOND cue, never the only one: every segment keeps its text
 * label beside the glyph, and the icon is `aria-hidden` so the accessible
 * name is still exactly the label.
 */
const MODE_ICONS: Record<RunWorkspaceModeId, LucideIcon> = {
  dag: Network,
  molecule: Atom,
  torsion: Spline,
};

/**
 * One segment of the switch.
 *
 * A mode whose data does not exist yet is offered-but-disabled and says
 * *pending*, with the reason in its title — never hidden (which reads as "the
 * portal cannot do that") and never enabled-then-empty (which reads as a bug).
 * Carried over verbatim from the old visual selector; the three-state rule is
 * `run-visual-views.ts`'s, not restated here.
 *
 * The ACTIVE state carries three cues, and the two non-chromatic ones are
 * load-bearing — the same rule the rail's active row follows. The segment is
 * raised out of the well back to panel level (`bg-card`), which is a
 * measurable lightness step rather than a hue judgement; `shadow-sm` puts the
 * theme's own lift on it (a drop in light, a 1px top highlight in dark); and
 * its ink goes to `--foreground` from `--muted-foreground`. An inactive
 * segment paints NO background at all — the well behind it is the ground — so
 * exactly one segment in the group is ever a filled shape.
 */
function ModeButton({
  mode,
  active,
  onSelect,
}: {
  mode: RunWorkspaceMode;
  active: boolean;
  onSelect: () => void;
}): JSX.Element {
  const ready = mode.availability === "ready";
  const Icon = MODE_ICONS[mode.id];
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={!ready}
      aria-pressed={active}
      title={
        ready
          ? undefined
          : mode.availability === "pending"
            ? `${mode.label} — not produced yet`
            : `${mode.label} — this run produced none`
      }
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-medium transition-colors",
        active && ready ? "bg-card text-foreground shadow-sm" : "text-muted-foreground",
        ready ? "hover:text-foreground" : "cursor-not-allowed opacity-60"
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {mode.label}
      {mode.availability === "pending" && (
        <span className="text-[9px] uppercase tracking-wider">pending</span>
      )}
    </button>
  );
}

/** Summary states from which the flow could still produce something new.
 * Mirrors `POLLING_STATES` in `@/api/flow-run-dag` — and for the same
 * reason: `not_started` is also the state right after submit, before the
 * first row lands, so it is genuinely live. */
const LIVE_SUMMARY_STATES: ReadonlySet<FlowRunSummaryState> = new Set([
  "not_started",
  "running",
  "blocked",
]);

const SUMMARY_META: Record<
  FlowRunSummaryState,
  { icon: React.ComponentType<{ className?: string }>; classes: string }
> = {
  failed: {
    icon: AlertTriangle,
    classes:
      "border-state-failed-system-border bg-state-failed-system-bg text-state-failed-system-fg",
  },
  running: {
    icon: Loader2,
    classes: "border-state-running-border bg-state-running-bg text-state-running-fg",
  },
  blocked: {
    icon: Clock,
    classes: "border-state-staging-border bg-state-staging-bg text-state-staging-fg",
  },
  completed: {
    icon: CheckCircle2,
    classes: "border-state-completed-border bg-state-completed-bg text-state-completed-fg",
  },
  not_started: {
    icon: Clock,
    classes: "border-border-subtle bg-surface-2 text-muted-foreground",
  },
};

/**
 * Top-of-page "running / blocked at / failed at <node>" headline — and WHOSE
 * state it is (flowstack#1223 A).
 *
 * `summary` is always the FLOW's, never this page's run: `_derive_summary`
 * projects every node in the cohort. On a child run's page the header above
 * badges something else entirely — that run's own lifecycle state — so the two
 * sat on one screen as "completed" and "Failed at merge_fragments" with nothing
 * reconciling them. Both were true. Neither said what it was about.
 *
 * So on a child run this banner states its subject before its verdict, names
 * the step this page IS where the cohort can tell us (`nodeNameOfRun`), and
 * absorbs the breadcrumb that used to be a separate row above it — the link to
 * the parent belongs to the sentence about the flow, not beside it. On the
 * flow's own run there is only one subject and the banner is unchanged.
 */
function FlowRunSummaryBanner({
  summary,
  parentRunId,
  nodeName,
}: {
  summary: FlowRunSummary;
  /** The cohort's root run when this page is a CHILD of it, else `null`. */
  parentRunId: string | null;
  /** Which node this page's run is, when the cohort says so. See `nodeNameOfRun`. */
  nodeName: string | null;
}): JSX.Element {
  const meta = SUMMARY_META[summary.state];
  const Icon = meta.icon;
  const isChild = parentRunId !== null;
  return (
    <div
      className={cn(
        "flex items-start gap-2.5 rounded-lg border px-4 py-3 text-sm font-medium",
        meta.classes
      )}
      data-testid="flow-run-summary"
    >
      <Icon
        className={cn("mt-0.5 h-4 w-4 shrink-0", summary.state === "running" && "animate-spin")}
        aria-hidden
      />
      <div className="flex min-w-0 flex-col gap-1">
        <span>
          {isChild && (
            // The SUBJECT, set apart from the verdict rather than folded into
            // it: `summary.message` is the server's own sentence and is left
            // verbatim, so this cannot drift from what `_derive_summary` says.
            <span className="label-micro mr-2 opacity-80" data-testid="flow-run-summary-scope">
              Flow run
            </span>
          )}
          {summary.message}
        </span>
        {isChild && (
          <span className="text-xs font-normal opacity-90">
            {nodeName === null
              ? "This page is one step of that flow; the state beside its title is that step's own."
              : `This page is that flow's ${nodeName} step; the state beside its title is that step's own.`}{" "}
            Part of flow run{" "}
            <Link
              to={`/runs/${encodeURIComponent(parentRunId)}`}
              className="font-mono underline underline-offset-2"
            >
              {parentRunId}
            </Link>
            .
          </span>
        )}
      </div>
    </div>
  );
}

type SortKey = "name" | "status" | "duration_seconds";
type SortDir = "asc" | "desc";

const TOPOLOGY_LABEL: Record<"scope" | "match", string> = {
  // Human-friendly names for the internal SCOPE/MATCH fanout kinds —
  // UI-only, the YAML keywords (`over`/`as`, `match`) are unchanged.
  scope: "Scatter",
  match: "Aligned",
};

/** Node status table — node / tile / topology / state / duration /
 * cache / child run, sortable by clicking a header. */
function FlowRunNodeTable({ nodes }: { nodes: FlowRunNodeState[] }): JSX.Element {
  const [sort, setSort] = React.useState<{ key: SortKey; dir: SortDir }>({
    key: "name",
    dir: "asc",
  });

  const toggleSort = (key: SortKey): void => {
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  };

  const sorted = React.useMemo(() => {
    const withIndex = nodes.map((node, index) => ({ node, index }));
    withIndex.sort((a, b) => {
      const cmp = compareBy(sort.key, a.node, b.node);
      if (cmp !== 0) return sort.dir === "asc" ? cmp : -cmp;
      return a.index - b.index; // stable fallback — declaration order
    });
    return withIndex.map((x) => x.node);
  }, [nodes, sort]);

  return (
    <Card className="p-2">
      <Table role="table">
        <TableCaption>Per-node status for this flow run</TableCaption>
        <TableHeader>
          <TableRow>
            <SortableHeader label="Node" sortKey="name" sort={sort} onSort={toggleSort} />
            <TableHead>Tile</TableHead>
            <TableHead>Topology</TableHead>
            <SortableHeader label="State" sortKey="status" sort={sort} onSort={toggleSort} />
            <SortableHeader
              label="Duration"
              sortKey="duration_seconds"
              sort={sort}
              onSort={toggleSort}
            />
            <TableHead>Cache</TableHead>
            <TableHead>Child run</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((node) => (
            <TableRow key={node.name} interactive="hover">
              <TableCell className="font-mono text-xs">{node.name}</TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground">{node.tile}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {node.fanout_kind ? TOPOLOGY_LABEL[node.fanout_kind] : "—"}
              </TableCell>
              <TableCell>
                {node.status ? (
                  <div className="flex items-center gap-1.5">
                    <RunStateBadge state={node.status} />
                    {node.child_count > 1 && (
                      <span className="font-mono text-[10px] text-muted-foreground">
                        ×{node.child_count}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">not started</span>
                )}
              </TableCell>
              <TableCell className="font-mono text-xs tabular-nums">
                {formatDuration(node.duration_seconds)}
              </TableCell>
              <TableCell className="text-xs">
                {node.cache_hit === null ? "—" : node.cache_hit ? "hit" : "miss"}
              </TableCell>
              <TableCell className="text-xs">
                {node.child_count === 1 && node.run_id ? (
                  <Link
                    to={`/runs/${encodeURIComponent(node.run_id)}`}
                    className="text-foreground hover:underline"
                  >
                    View run →
                  </Link>
                ) : node.child_count > 1 ? (
                  <span className="text-muted-foreground">
                    {node.child_count.toLocaleString()} runs
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function compareBy(key: SortKey, a: FlowRunNodeState, b: FlowRunNodeState): number {
  if (key === "name") return a.name.localeCompare(b.name);
  if (key === "status") return (a.status ?? "").localeCompare(b.status ?? "");
  // duration_seconds — nulls sort last regardless of direction, since
  // "no duration yet" isn't meaningfully smaller or larger than a
  // real value.
  const av = a.duration_seconds;
  const bv = b.duration_seconds;
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;
  return av - bv;
}

function SortableHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey; dir: SortDir };
  onSort: (key: SortKey) => void;
}): JSX.Element {
  const active = sort.key === sortKey;
  return (
    <TableHead>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          "inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground",
          active && "text-foreground"
        )}
        aria-label={`Sort by ${label}`}
      >
        {label}
        <ArrowUpDown className={cn("h-3 w-3", active ? "opacity-100" : "opacity-40")} aria-hidden />
      </button>
    </TableHead>
  );
}
